import React, { useCallback } from "react";
import { Alert, Button, Form, Input, InputNumber, Space, Switch, Typography } from "antd";
import { SectionPanelProps } from "./SectionTypes";
import { useJsonSection } from "./useJsonSection";
import { ClientConfig } from "../../../model/client/ClientSettings";
import { JoiningFeeConfig, parseJoiningFee, DEFAULT_JOINING_FEE } from "../../../components/membership/joiningFee";
import { CopyFromClientButton } from "./CopyFromClientModal";

const { Text } = Typography;

export const FEES_JOINING_KEY = 'fees.joining';

// Settings panel for the configurable joining fee (fees.joining). Mirrors the
// ThemeSectionPanel load/dirty/save lifecycle via the shared useJsonSection hook.
export const JoiningFeeSectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const extract = useCallback(
        (config: ClientConfig | undefined) => parseJoiningFee(config?.settings.fees?.joiningJson),
        [],
    );
    const { clientId, value, setValue, initial, loadError, saveError, saving, save, reload } =
        useJsonSection<JoiningFeeConfig>(extract);

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="settings-joining-load-error" />;
    }
    if (value === null || initial === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    const update = (patch: Partial<JoiningFeeConfig>) => setValue({ ...value, ...patch });
    const dirty = JSON.stringify(initial) !== JSON.stringify(value);
    const valid = value.amount != null && value.amount >= 0 && value.posItemName.trim().length > 0;

    const handleSave = () =>
        save([{ key: FEES_JOINING_KEY, value: JSON.stringify(value), valueType: 'json' }], value);

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <CopyFromClientButton
                targetClientId={clientId}
                groups={['fees']}
                onCopied={reload}
            />
            <Form layout="vertical" data-testid="settings-joining-form">
                <Form.Item label="Charge a joining fee">
                    <Switch
                        data-testid="settings-joining-enabled"
                        checked={value.enabled}
                        onChange={(checked) => update({ enabled: checked })}
                    />
                </Form.Item>
                <Form.Item label="Amount" required>
                    <InputNumber
                        data-testid="settings-joining-amount"
                        inputMode="decimal"
                        min={0}
                        step={1}
                        value={value.amount}
                        onChange={(v) => update({ amount: typeof v === 'number' ? v : 0 })}
                    />
                </Form.Item>
                <Form.Item label="Label">
                    <Input
                        data-testid="settings-joining-label"
                        value={value.label}
                        placeholder={DEFAULT_JOINING_FEE.label}
                        onChange={(e) => update({ label: e.target.value })}
                    />
                </Form.Item>
                <Form.Item
                    label="POS item name"
                    required
                    help="The point-of-sale item used as the line item on the initial payment."
                >
                    <Input
                        data-testid="settings-joining-pos-item"
                        value={value.posItemName}
                        placeholder={DEFAULT_JOINING_FEE.posItemName}
                        onChange={(e) => update({ posItemName: e.target.value })}
                    />
                </Form.Item>
            </Form>
            {saveError && <Alert type="error" message={saveError} data-testid="settings-joining-save-error" />}
            <Space>
                <Button
                    type="primary"
                    data-testid="settings-joining-save-btn"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!dirty || !valid}
                >
                    Save changes
                </Button>
                <Button
                    data-testid="settings-joining-discard-btn"
                    onClick={() => setValue(initial)}
                    disabled={!dirty || saving}
                >
                    Discard
                </Button>
            </Space>
        </Space>
    );
};
